import React, { useEffect, useState } from 'react';
import { IconButton, Stack, TextField } from '@mui/material';
import Icon from '@mdi/react';
import AppTooltip from '../AppTooltip/AppTooltip';
import { mdiAlertCircle, mdiContentSaveCheck, mdiUndo } from '@mdi/js';
import { bodyFont, vars } from '../../../Theme';
import { fetchUpdateImportDocuments } from '../../../util/ImportUtils';
import store, { IAppReduxStore } from '../../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setEditingActive } from '../../../redux/import/sliceImportData';
import { setDocuments } from '../../../redux/import/sliceImportDocuments';

type SavingState = 'default' | 'pending' | 'succeed' | 'failed';

const config = {
    stateDurations: {
        succeed: 1500,
        failed: 3000,
    },
    endAdornment: {
        succeed: <Icon path={mdiContentSaveCheck} color={vars.brandGreen} size={1} />,
        failed: <Icon path={mdiAlertCircle} color={vars.errorRed} size={1} />,
    },
};

export default ({
    replicaId,
    docId,
    value,
    notesField,
}: {
    replicaId: string;
    docId: string;
    value: string;
    notesField: string;
}) => {
    const { t } = useTranslation('translations', { keyPrefix: 'importTableEditableCell' });
    const dispatch = useDispatch();
    const containerRef = React.useRef<HTMLDivElement>();
    const inputRef = React.useRef<HTMLInputElement>();
    const [savingState, setSavingState] = useState<SavingState>('default');
    const [editMode, setEditMode] = useState<boolean>(false);
    const [originalValue, setOriginalValue] = useState<string>(value);

    const importView = useSelector((store: IAppReduxStore) => store.importData.currentView);

    const onStartEdit = () => {
        setEditMode(true);
        dispatch(setEditingActive(true));
        inputRef?.current?.focus();
    };

    useEffect(() => {
        // Notifiy controlled field.
        const newValue = value || '';

        setOriginalValue(newValue);

        inputRef.current.value = newValue;
    }, [value]);

    useEffect(() => {
        if (['succeed', 'failed'].includes(savingState)) {
            setTimeout(() => setSavingState('default'), config.stateDurations[savingState]);
        }
    }, [savingState]);

    const onSaveEdit = async () => {
        setEditMode(false);
        dispatch(setEditingActive(false));

        const newValue = inputRef.current.value;

        if (newValue === originalValue) {
            return;
        }

        setOriginalValue(newValue);

        const update = [
            {
                UniversalID: docId,
                [notesField]: newValue,
            },
        ];

        setSavingState('pending');

        try {
            await fetchUpdateImportDocuments(replicaId, importView, JSON.stringify(update));
            setSavingState('succeed');

            const newDocs = store.getState().importDocuments.data.map(doc => {
                if (doc.UniversalID === docId) {
                    return { ...doc, [notesField]: newValue };
                }
                return doc;
            });

            dispatch(setDocuments(newDocs));
        } catch {
            setSavingState('failed');
        }
    };

    const onCancelEdit = () => {
        inputRef.current.value = originalValue;
        setEditMode(false);
        dispatch(setEditingActive(false));
    };

    const inputField = () => {
        return (
            <AppTooltip title={originalValue || ''}>
                <TextField
                    disabled={savingState === 'pending'}
                    inputRef={inputRef}
                    slotProps={{
                        input: {
                            readOnly: !editMode,
                            endAdornment: config.endAdornment[savingState],
                        },
                    }}
                    onKeyDown={e => {
                        e.stopPropagation();
                        e.key === 'Escape' && onCancelEdit();
                    }}
                    defaultValue={value}
                    sx={{
                        '& .MuiInputBase-root': {
                            height: '100%',
                        },
                        '& .MuiInputBase-input': {
                            fontFamily: bodyFont,
                            fontSize: '14px',
                            padding: 0,
                            color: savingState === 'failed' ? vars.errorRed : undefined,
                        },
                        '& fieldset': { border: 'none' },
                        border: 'none',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        width: '100%',
                        height: '100%',
                    }}
                />
            </AppTooltip>
        );
    };

    const undoButton = () => {
        return (
            <AppTooltip title={t('reset')}>
                <IconButton onClick={onCancelEdit} sx={{ borderRadius: 1 }}>
                    <Icon path={mdiUndo} size={1} color={vars.font} />
                </IconButton>
            </AppTooltip>
        );
    };

    return (
        <Stack
            ref={containerRef}
            direction='row'
            onFocus={() => onStartEdit()}
            onBlur={e => {
                if (!containerRef.current.contains(e.relatedTarget)) {
                    onSaveEdit();
                }
            }}
            sx={{
                padding: '0px 10px',
                height: '100%',
                width: '100%',
                alignItems: 'center',
            }}
        >
            {inputField()}
            {editMode && undoButton()}
        </Stack>
    );
};
